import React, { useState } from "react";

import { t } from "i18next";
import { Pencil } from "lucide-react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import OfferPlanDrawer from "./drawer/OfferPlanDrawer";
import { OFFER_PLAN_TYPES } from "./offerPlan.constants";
import { apiToPlanForm } from "./offerPlanAdapter";
import { getPlanGeoblockLabel } from "./offerPlanView";
import { useOfferPlanMutations } from "../../hooks/useOfferPlanMutations";
import { getSelectedCountries } from "../../utils/data/Countries";
import { getNewEduOfferPlan } from "../../utils/eduOfferPlan";
import { isGeoblockActive } from "../../utils/geoblock";
import { Button } from "../ui/button";

import type { JSX, ReactNode } from "react";

import type { FlatOfferPlan } from "./offerPlanAdapter";
import type { ProviderPlan } from "../../api/OfferAPI.types";
import type { AppState } from "../../reducers";
import type { OfferPlanType } from "../../types";

interface EduOfferConfigurationProps {
  plan?: OfferPlanType;
  onPlanAdd: (plan: FlatOfferPlan) => void;
  onPlanChange: (plan: FlatOfferPlan) => void;
}

interface SummaryItemProps {
  label: string;
  children: ReactNode;
}

// Stable reference: the drawer memoizes on it.
const NO_PROVIDER_PLAN_LIST: ProviderPlan[] = [];

function SummaryItem({ label, children }: SummaryItemProps): JSX.Element {
  return (
    <div className="flex min-w-0 flex-1 basis-48 flex-col gap-1">
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="text-sm font-medium break-words">{children}</dd>
    </div>
  );
}

function EduOfferConfiguration({
  plan,
  onPlanAdd,
  onPlanChange,
}: EduOfferConfigurationProps): JSX.Element {
  const { projectSlug, id: offerId } = useParams<{
    projectSlug: string;
    id: string;
  }>();
  const countries = useSelector(
    (state: AppState) => state.fetchReducer.countries,
  );
  const allowSponso = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options?.allow_sponso,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { savePlan, isSaving } = useOfferPlanMutations({
    projectSlug: projectSlug ?? "",
    offerId: offerId ?? "",
  });

  // Fallback when the plan couldn't be created along with the offer.
  const handleCreatePlan = async (): Promise<void> => {
    const newPlan = apiToPlanForm(getNewEduOfferPlan());

    try {
      const { uuid } = await savePlan(newPlan);
      onPlanAdd({ ...newPlan, uuid, isNew: false });
    } catch {
      // Save failed — the mutation's onError already surfaced a toast.
    }
  };

  if (!plan) {
    return (
      <div className="flex flex-col items-start gap-4">
        <p className="text-muted-foreground text-sm">
          {t("editor-offers:noConfiguration")}
        </p>
        <Button disabled={isSaving} onClick={handleCreatePlan} type="button">
          {t("editor-offers:createConfiguration")}
        </Button>
      </div>
    );
  }

  const restrictionsLabel = isGeoblockActive(plan.geoblock_mode ?? "disabled")
    ? getPlanGeoblockLabel(
        plan,
        getSelectedCountries(countries, plan.countries ?? []).map(
          ({ label }) => label,
        ),
      )
    : t("editor-offers:noRestriction");

  return (
    <React.Fragment>
      <div className="flex items-start justify-between gap-6">
        <dl className="flex min-w-0 flex-1 flex-wrap gap-x-8 gap-y-5">
          <SummaryItem label={t("editor-offers:resolution")}>
            {plan.resolution.toUpperCase()}
          </SummaryItem>
          <SummaryItem label={t("editor-offers:section_restrictions")}>
            {restrictionsLabel}
          </SummaryItem>
          {allowSponso ? (
            <SummaryItem label={t("editor-offers:monetizationStatus")}>
              {plan.advertising?.label ?? t("editor-offers:noCampaign")}
            </SummaryItem>
          ) : null}
        </dl>
        <Button
          onClick={() => setIsDrawerOpen(true)}
          type="button"
          variant="outline"
        >
          <Pencil size={16} />
          {t("editor-offers:edit")}
        </Button>
      </div>
      <OfferPlanDrawer
        availablePlatform={{}}
        availableProviderPlanList={NO_PROVIDER_PLAN_LIST}
        availableRetailerList={[]}
        offerId={offerId ?? ""}
        onClose={() => setIsDrawerOpen(false)}
        onSaved={onPlanChange}
        open={isDrawerOpen}
        plan={plan}
        planType={OFFER_PLAN_TYPES.SVOD}
        projectSlug={projectSlug ?? ""}
      />
    </React.Fragment>
  );
}

export default EduOfferConfiguration;
